import { ConsentGroups, isGroupActive } from '@fe-toolkit/analytics'
import { captureLandingContext } from '@fe-toolkit/utility/client/tracking-context'

import { buildGgUtmzCookie, GG_UTMZ_COOKIE, resolveGgUtmzDecision } from './campaign'

/**
 * Keeps the `__ggUtmz` Portal bridge in step with OneTrust consent (FAAS-782).
 *
 * The SSR middleware can only act on consent as it stood when the document was
 * requested, which misses the journey that matters most: someone arrives from a
 * campaign with no consent cookie at all, the banner appears, they accept, and
 * then they walk into a Portal journey. No further document request happens in
 * between, so without this the cookie is never written and the order loses its
 * attribution. That is the ordinary path for a new visitor from an ad, not an
 * edge case.
 *
 * The reverse matters more. Portal reads the cookie's absence as a consent
 * signal (`HandsetOrderDetails.php:125` reports `utm_campaign` as the literal
 * `"no-consent"`), so leaving a cookie in place after someone withdraws consent
 * would keep them looking consented. Withdrawal expires it here.
 *
 * Reads the landing query string from the toolkit's in-memory landing snapshot
 * rather than the current URL, so consent granted several SPA navigations later
 * still records the campaign the visitor actually arrived on.
 */
export function syncGgUtmzConsent(cookieDomain: string): void {
  if (typeof document === 'undefined' || !cookieDomain) {
    return
  }

  const consented = isGroupActive(ConsentGroups.targeting)
  const landing = captureLandingContext()

  // Same chain as the SSR middleware, so consenting on the banner records the source the
  // visitor actually arrived from rather than only a tagged campaign.
  //
  // Referrer comes from the landing snapshot, not document.referrer, for the same reason
  // the search string does: consent is often granted several SPA navigations in, by which
  // point document.referrer no longer describes how the visit started. Only the hostname
  // of currentUrl is read, so window.location is safe there.
  const decision = consented
    ? resolveGgUtmzDecision({
        search: landing?.search ?? '',
        referrer: landing?.referrer,
        currentUrl: window.location.href,
        hasExistingCookie: document.cookie.includes(`${GG_UTMZ_COOKIE}=`),
      })
    : undefined

  if (decision?.action === 'keep') {
    return
  }

  if (decision) {
    document.cookie = buildGgUtmzCookie(decision.value, {
      cookieDomain,
      secure: window.location.protocol === 'https:',
    })

    return
  }

  // Only expire a cookie that exists. Writing an expiry unconditionally is
  // harmless but noisy, and on a first visit with no campaign there is nothing
  // to clear.
  if (!consented && document.cookie.includes(`${GG_UTMZ_COOKIE}=`)) {
    document.cookie = buildGgUtmzCookie(null, {
      cookieDomain,
      secure: window.location.protocol === 'https:',
    })
  }
}
